import React, { useRef, useState } from 'react';
import {
  AvDateRangePicker,
  Filter,
  FilterStyledPopper,
  formatDate,
  getDatePickerFromQueryObject,
  getStringConditionFromDatePicker,
  isDateRangeEmpty,
  isDateRangeNotEmpty,
  selectorWidth,
  TextInput,
} from '@fabric-ui/components';
import { Box, capitalize, ClickAwayListener } from '@mui/material';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { useFilterConfig } from './FilterContext';

const getEmptyOrNotEmptyDateValue = (filter: Filter) =>
  filter?.expression?.stringCondition
    ? 'empty' in filter.expression.stringCondition
      ? 'empty'
      : 'notEmpty' in filter.expression.stringCondition
        ? 'notEmpty'
        : null
    : null;

const getDisplayValue = (
  dateValue:
    | ReturnType<typeof getEmptyOrNotEmptyDateValue>
    | NonNullable<ReturnType<typeof getDatePickerFromQueryObject>>
    | {
        value: {
          from: Date;
          to: Date;
        };
      }
) => {
  if (typeof dateValue === 'string' && isDateRangeNotEmpty(dateValue)) {
    return 'Is Not Empty';
  }
  if ((typeof dateValue === 'string' && isDateRangeEmpty(dateValue)) || dateValue === null) {
    return 'Is Empty';
  }

  return 'preset' in dateValue && dateValue.preset?.type && dateValue.preset?.period
    ? `${capitalize(dateValue.preset.type)} ${dateValue.preset.count} ${capitalize(dateValue.preset.period)}`
    : [formatDate(dateValue.value.from, 'MMM dd, yyyy'), formatDate(dateValue.value.to, 'MMM dd, yyyy')].join(' - ');
};

interface DateTypeBoxProps {
  filter: Filter;
  setFilter: (filter: Filter) => void;
}
const DateTypeBox: React.FC<DateTypeBoxProps> = ({ filter, setFilter }) => {
  const { size, isVertical, disabled, containedDatePicker } = useFilterConfig();
  const { readOnly } = useExpressionBuilder();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const field = (filter.expression?.arrayCondition?.underlying || filter.expression)?.fieldName;

  const handleClose = () => {
    setOpen(false);
  };

  const dateValue = getEmptyOrNotEmptyDateValue(filter) ||
    getDatePickerFromQueryObject(filter) || {
      value: {
        from: new Date(),
        to: new Date(),
      },
    };

  const displayValue = getDisplayValue(dateValue);

  return (
    <Box key={field}>
      <Box ref={buttonRef} onClick={() => (!disabled ? setOpen(!open) : {})}>
        <TextInput
          size={size}
          style={{ width: isVertical ? selectorWidth : 250 }}
          disableClearButton
          placeholder="date"
          value={displayValue}
          disabled={disabled}
          inputProps={{ readOnly }}
        />
      </Box>
      <FilterStyledPopper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        disablePortal={containedDatePicker}
        sx={{
          minWidth: '400px',
          width: 'fit-content',
          padding: 0,
        }}>
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <AvDateRangePicker
              value={
                dateValue && typeof dateValue !== 'string'
                  ? { from: new Date(dateValue.value.from), to: new Date(dateValue.value.to) }
                  : dateValue
              }
              activePresetValue={dateValue && typeof dateValue !== 'string' && 'preset' in dateValue ? dateValue.preset : undefined}
              onChange={newDate => {
                const expression = {
                  fieldName: filter.expression?.fieldName,
                  ...getStringConditionFromDatePicker(newDate),
                };
                setFilter({
                  ...filter,
                  expression,
                });
              }}
              onClose={handleClose}
              showAsCalendar
              isRange
              showRelativeOptions
              isFilterExpression
            />
          </div>
        </ClickAwayListener>
      </FilterStyledPopper>
    </Box>
  );
};

export default DateTypeBox;
